import { CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition, ConnectionPositionPair } from '@angular/cdk/overlay';
import { NgTemplateOutlet, TitleCasePipe } from '@angular/common';
import { Component, computed, effect, inject, input, OnDestroy, output, viewChild, ViewEncapsulation } from '@angular/core';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatChip, MatChipAvatar, MatChipListbox } from '@angular/material/chips';
import { MatIcon } from '@angular/material/icon';
import { MatDivider } from '@angular/material/list';
import { MatTooltip } from '@angular/material/tooltip';
import { ObjectTypes } from '@common/enums/object-types';
import { Store } from '@ngrx/store';
import { AppService } from '@src/suite/app.service';
import { DraggableEnpsComponent } from '@src/suite/common/components/base/draggable-enps-component';
import { PSTFieldNames } from '@src/suite/common/data-models/fields/pst-field-names';
import { FieldDefValidations } from '@src/suite/common/enums/enums';
import { HighlightPipe } from '@src/suite/common/pipes/highlight';
import { GlobalTablesService } from '@src/suite/common/services/global-tables-service';
import { VisualStateService } from '@src/suite/common/services/visual-state-service';
import { selectCastViewStateCustomCardItems } from '@src/suite/common/store/cast/cast.selectors';
import { selectClientTimeZone, selectPersonalTimeZone } from '@src/suite/common/store/global-tables/global-tables.selectors';
import { selectI18N } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { selectViewStateSearchString } from '@src/suite/common/store/navigation/selectors/viewstate.selectors';
import { RecordHttpActions } from '@src/suite/common/store/records/records.actions';
import { getItemString } from '@src/suite/common/utils/util';

import { AssignmentTypeComponent } from '../../controls/assignment-type/assignment-type';
import { CheckBoxComponent } from '../../controls/check-box/check-box';
import { StTextAreaOverlayComponent } from '../../controls/st-text-area/text-area-overlay/st-text-area-overlay';
import { TopicTypeComponent } from '../../controls/topic-type/topic-type';
import { ClickStopPropagationDirective } from '../../directives/stop-propagation';
import { AvatarSmallComponent } from '../avatar/avatar-small';
import { IconComponent } from '../icon/icon';

@Component({
    selector: 'st-cast-object-item',
    imports: [
        AssignmentTypeComponent,
        AvatarSmallComponent,
        CdkConnectedOverlay,
        CdkOverlayOrigin,
        CheckBoxComponent,
        ClickStopPropagationDirective,
        IconComponent,
        MatButton,
        MatChip,
        MatChipAvatar,
        MatChipListbox,
        MatDivider,
        MatIcon,
        MatIconButton,
        MatTooltip,
        NgTemplateOutlet,
        StTextAreaOverlayComponent,
        TitleCasePipe,
        TopicTypeComponent,
    ],
    templateUrl: './cast-object-item.html',
    styleUrl: './cast-object-item.scss',
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-cast-object',
    },
})
export class CastObjectItemComponent extends DraggableEnpsComponent implements OnDestroy {
    ObjectTypesEnum = ObjectTypes;
    isPanelOpen = false;
    PSTFieldNames = PSTFieldNames;
    readonly FieldDefValidations = FieldDefValidations;
    public readonly getItemString = getItemString;

    globalTablesService = inject(GlobalTablesService);
    appService = inject(AppService);
    store = inject(Store);
    visualStateService = inject(VisualStateService);
    highlightPipe = inject(HighlightPipe);

    readonly isLast = input(false);

    readonly customCardItems = input<FieldDef[]>([]);
    public itemStringCache = new Map<string, string>();

    readonly data = input<StorytellingAssignmentRecord | StorytellingTopicRecord>();

    languageSettings = this.store.selectSignal(selectI18N);
    i18n = this.store.selectSignal(selectI18N);
    searchString = this.store.selectSignal(selectViewStateSearchString);
    personalTimeZone = this.store.selectSignal(selectPersonalTimeZone);
    clientTimeZone = this.store.selectSignal(selectClientTimeZone);
    viewStateCustomItems = this.store.selectSignal(selectCastViewStateCustomCardItems);

    // _disabled: boolean;

    // @Input() get disabled(): boolean {
    //     return this._disabled;
    // }
    // set disabled(value: boolean) {
    //     if (this.data().PSTApproved) {
    //         value = this.data().PSTApproved;
    //     }
    //     this._disabled = value;
    // }

    readonly overlay = viewChild(CdkConnectedOverlay);
    readonly itemClick = output<StorytellingTopicRecord>();

    constructor() {
        super();
        effect(() => {
            if (this.data()) {
                this.itemStringCache.clear();
            }
        });
        // effect(() => {
        //     // this._guid = `candidate|${this.data()?.Guid}`;
        // });
    }

    readonly ownersLabel = computed(() => {
        if (this.data().Type === ObjectTypes.TOPIC) {
            return this.globalTablesService.FieldDefsDict[PSTFieldNames?.PSTTopicStoryOwner]?.DefaultCaption;
        }
        if (this.data().Type === ObjectTypes.SCRIPT) {
            return this.globalTablesService.FieldDefsDict[PSTFieldNames.PSTStoryOwner]?.DefaultCaption;
        }
        return this.globalTablesService.FieldDefsDict[PSTFieldNames.PSTAssignmentOwner]?.DefaultCaption;
    });

    readonly owners = computed(() => {
        if (this.data().Type === ObjectTypes.TOPIC) {
            return (this.data() as StorytellingTopicRecord).PSTTopicOwner;
        }
        if (this.data()?.Type === ObjectTypes.SCRIPT) {
            return (this.data() as StorytellingStoryRecord)?.PSTStoryOwner;
        }
        return (this.data() as StorytellingAssignmentRecord)?.PSTAssignmentOwner;
    });

    readonly status = computed(() => {
        if (this.data().Type === ObjectTypes.TOPIC) {
            return (this.data() as StorytellingTopicRecord).PSTTopicStatus;
        }
        if (this.data()?.Type === ObjectTypes.SCRIPT) {
            return (this.data() as StorytellingStoryRecord)?.PSTStoryStatus;
        }
        return (this.data() as StorytellingAssignmentRecord)?.PSTAssignmentStatus;
    });

    getInnerProp(item: FieldDef) {
        const val = this.data()?.[item.ID.split('-')[0]];
        if (val) return val[item.ComplexPropInnerId];
        return '';
    }

    readonly currentTimeZone = computed(() => {
        return this.personalTimeZone() ? this.personalTimeZone() : this.clientTimeZone();
    });

    readonly assigmentType = computed(() => {
        return (this.data() as StorytellingAssignmentRecord)?.PSTCoverageType;
    });

    readonly desk = computed(() => {
        return this.data().PSTGroup;
    });
    readonly approvedLabel = computed(() => {
        if (this.data()?.Type === ObjectTypes.TOPIC || this.data()?.Type === ObjectTypes.ASSIGNMENT || this.data()?.Type === ObjectTypes.SCRIPT) {
            return this.data().PSTApproved ? 'Approved' : 'Approve';
        }
    });

    readonly assignment = computed(() => {
        if (this.data()?.Type === ObjectTypes.ASSIGNMENT) {
            return this.data();
        }
        return null;
    });

    readonly assignmentIconType = computed(() => {
        if (this.data()?.Type === ObjectTypes.ASSIGNMENT) {
            return (this.data() as StorytellingAssignmentRecord)?.PSTCoverageType?.id;
        }
        return null;
    });

    readonly getItemCreator = computed(() => {
        return `${this.languageSettings().PSTWebCastObjectsCreatedBy} ${this.data().Creator}`;
    });

    get connectedPositions(): ConnectedPosition[] {
        return [
            new ConnectionPositionPair({ originX: 'start', originY: 'top' }, { overlayX: 'end', overlayY: 'top' }),
            new ConnectionPositionPair({ originX: 'start', originY: 'bottom' }, { overlayX: 'end', overlayY: 'bottom' }),
        ];
    }

    // TODO: Probably dont need it at all
    override ngOnDestroy(): void {
        super.ngOnDestroy();
        this.isPanelOpen = false;
    }

    public onMouseMove(event: MouseEvent) {
        if (!(event.relatedTarget as HTMLElement)?.classList?.contains('st-cast-object-item-container-overlay')) {
            this.isPanelOpen = false;
        }
    }

    panelAttached() {
        const overlayElement = this.overlay()?.overlayRef?.overlayElement;
        if (overlayElement) {
            overlayElement.addEventListener('mouseleave', () => {
                this.isPanelOpen = false;
            });
        }
    }

    updateTopic(value: any, fieldId: string) {
        const fieldDef = [{ value, key: fieldId }];
        // TODO: not cool to have this title logic here
        if (fieldId === PSTFieldNames.Title) {
            this.data().Title = value.trim();
        }
        this.store.dispatch(RecordHttpActions.saveRecordChanges({ record: this.data(), fieldsToUpdate: fieldDef }));
    }
}
